/**
 * `wayai knowledge cut-over`: a preview hub's agents moved from their knowledge resources to the
 * org's knowledge base (#5142 step 4, PR 3b; K9, K11, K13, K19).
 *
 * `wayai knowledge migrate` copied the knowledge into the base; this switches a preview hub's
 * agents over to it, on the preview only. Production follows when the hub is published: the steps
 * are printed, never run.
 *
 * **Access stays per agent (K9).** Each distinct set of knowledge an agent reads today — the
 * file types of its enabled knowledge links — gets one read-only toolset on the knowledge base
 * (`list` and `get` on each file type; no agent writes knowledge today). Every file type is an org
 * resource's or this hub's own, the bound a hub's toolset keeps (K3). The toolset is written in
 * the base's preview and promoted, and the hub reaches it through one Base Toolset connection
 * holding a token bound to it on the preview base. Each agent is given `list_files`, `read_file`
 * and its toolset's `list_`/`get_` tools, which mounts its documents under `data/<file type>/`.
 * Migrate writes files in production only, so the run stops unless the preview base holds every
 * file production holds for each planned file type (`wayai knowledge refresh-preview` copies them).
 *
 * **Paths (K13).** A citation `resources/<slug>/…` of a moved resource in an agent's instructions,
 * its additional context, or an automation's `run_agent` instructions becomes
 * `data/<file type>/…`; the path within it is unchanged, as migrate kept it.
 *
 * **Placeholders (K19).** A resource placeholder that reaches knowledge an agent is about to lose
 * — `{{files()}}`/`{{files('kb')}}` (or `{{resources()}}`), and `{{resource_content()}}` or
 * `{{file_content()}}` naming such a resource — would render empty after the cut-over. It stops the
 * run before any write, naming the agent, so the operator moves that text into its instructions
 * or a skill first.
 *
 * **The sources (K11).** Each move is recorded first (`POST /api/setup/knowledge/moves`): the hub's
 * own moved resources, which the record marks read-only, and each org resource whose link this run
 * detaches. Only the org's registry can judge a claim (K8), so its refusal stops the run before any
 * toolset, connection, tool, text or link is written; the moves recorded ahead of the refused one
 * stay (each such resource is read-only, still read through its links). Once the claim is
 * resolved, a re-run skips the hub's own (marked moved) and sends an org resource's again while its link
 * is enabled, which the registry answers as held, writing nothing. The agents' knowledge links are disabled last.
 *
 * **Which file type a resource moved to.** Migrate names each file type's source in its schema
 * (`provenanceOf`): an org resource by id, a hub resource by its PRODUCTION hub's and resource's
 * ids. A preview's own resource is paired with production's by name, which is exact only while the
 * preview's knowledge resources equal production's, so the run stops unless the publish diff shows
 * none of them changed (and names are unique). A resource already marked moved says its file type.
 *
 * **Idempotent.** Everything is read first and only what differs is written: an existing toolset
 * equal to its plan, a connection already reaching it, a tool the agent already holds, a text with
 * nothing left to rewrite, a marked resource and a disabled link are each left alone. An agent's
 * set is the file types of its enabled links and of the cut-over toolsets it already holds tools
 * on, so a re-run after a partial cut-over lands every agent on the toolset the first run planned.
 * A re-run of a finished cut-over writes nothing.
 */

import { createHash } from 'node:crypto';
import { isDeepStrictEqual } from 'node:util';
import { scanTemplateResources, slugifySkillName } from '@wayai/core';
import { getConnectorById, getConnectors, isRekorToolsetConnector, isRekorToolsetConnectorId } from '@wayai/core/catalog';
import type { AutomationAction } from '@wayai/core/contracts/automations';
import type { HubDiffResult } from '@wayai/core/contracts';
import { TOOLSET_SLUG_SETTING, toolsetFileToolName } from '@wayai/core/contracts/rekor-toolset';
import type { RecordKnowledgeMoveInput } from '@wayai/core/contracts/setup/knowledge-moves';
import {
  RESOURCE_NAMESPACE,
  buildResourceSlugMap,
  cleanRelativePath,
  resourceDisplayName,
} from '@wayai/core/resource-paths';
import { expected } from '../lib/expected.js';
import { sanitizeTerminalText } from '../lib/terminal-output.js';
import { ApiError } from '../lib/api-client.js';
import { promotionProblems, refreshPreviewCommand, resolveBases, type HubListing, type KnowledgeBase, type MigrateIo } from './migrate.js';
import { hubPartOf, provenanceOf, toolBase } from './plan.js';
import { hubSourceKey, orgSourceKey } from './source.js';

/** Every toolset, and Base Toolset connection, this command writes is named `knowledge-<hash>`. */
export const CUT_OVER_TOOLSET_PREFIX = 'knowledge-';

/** Exactly the slugs {@link toolsetSlug} makes: a toolset someone named `knowledge-…` by hand is never taken for one. */
const CUT_OVER_TOOLSET_RE = /^knowledge-[0-9a-f]{12}$/;

/** The operations a cut-over toolset grants on each file type: read-only (K9). */
const FILE_OPERATIONS = ['list', 'get'] as const;

/** The file tools whose `data/` mount the toolset's `list` and `get` tools serve. */
const MOUNT_TOOLS = ['list_files', 'read_file'] as const;

/** The mount path a moved resource's files are read at: `data/<file type>/<path>`. */
const DATA_NAMESPACE = 'data';

/** A hub resource as the setup routes list it, with the mark a recorded move leaves on it. */
export interface CutOverResource {
  resource_id: string;
  resource_name: string;
  resource_type: string;
  skill_name: string | null;
  created_at: string;
  org_resource_id?: string | null;
  moved_file_type?: string | null;
}

export interface CutOverAgent {
  agent_id: string;
  agent_name: string;
  /** The connection running it: a hosted harness's takes no Base Toolset's tools. */
  connection_id: string | null;
  instructions: string | null;
  additional_context_template: string | null;
}

export interface AgentLink {
  resource_id: string;
  resource_type: string;
  enabled: boolean;
}

/** One tool an agent holds (`Disable`: assigned and enabled; `Enable`: assigned, disabled) or may add. */
export interface AgentTool {
  tool_id: string | null;
  tool_name: string;
  available_action: 'Add' | 'Enable' | 'Disable';
  /** A native tool's catalog id. */
  tool_native_id?: string | null;
  /** A connection tool's catalog row, and its connection. */
  mcp_tool_id?: string;
  connection_id?: string;
}

export interface AgentTools {
  native: AgentTool[];
  connection: AgentTool[];
}

export interface HubConnection {
  connection_id: string;
  connection_display_name: string | null;
  connector_id: string;
  settings: Record<string, unknown> | null;
}

export interface HubAutomation {
  automation_id: string;
  name: string;
  action: AutomationAction;
}

/** WayAI's side: the preview hub's setup routes. */
export interface HubSetup {
  /** The organization's hubs the caller can see, production and previews. */
  listHubs(): Promise<HubListing[]>;
  hubDiff(hubId: string): Promise<HubDiffResult>;
  listResources(hubId: string): Promise<CutOverResource[]>;
  listAgents(hubId: string): Promise<CutOverAgent[]>;
  listAgentLinks(hubId: string, agentId: string): Promise<AgentLink[]>;
  listAgentTools(hubId: string, agentId: string): Promise<AgentTools>;
  listConnections(hubId: string): Promise<HubConnection[]>;
  listAutomations(hubId: string): Promise<HubAutomation[]>;
  /** Adds a Base Toolset connection holding `token`; answers the hub's connections, the new one among them. */
  addToolsetConnection(hubId: string, input: { connectorId: string; name: string; toolset: string; token: string }): Promise<HubConnection[]>;
  syncConnection(hubId: string, connectionId: string): Promise<void>;
  addNativeTool(hubId: string, agentId: string, toolNativeId: string): Promise<void>;
  addConnectionTool(hubId: string, agentId: string, mcpToolId: string): Promise<void>;
  enableTool(hubId: string, agentId: string, toolId: string): Promise<void>;
  updateAgentText(hubId: string, agentId: string, patch: { instructions?: string; additional_context_template?: string }): Promise<void>;
  updateAutomationAction(hubId: string, automationId: string, action: AutomationAction): Promise<void>;
  disableLink(hubId: string, agentId: string, resourceId: string): Promise<void>;
  /** Records one move; false when the registry already held it. */
  recordMove(body: RecordKnowledgeMoveInput): Promise<boolean>;
}

/** One file tool of a toolset, as the data product stores it. */
export interface ToolsetFileEntry {
  file_type: string;
  operations: string[];
  name?: string;
  path_prefix?: string;
}

export interface RemoteToolset {
  id: string;
  name: string;
  description?: string | null;
  actions?: unknown[];
  relationships?: unknown[];
  files?: ToolsetFileEntry[];
  batch?: unknown;
  sql_query?: boolean;
}

export interface ToolsetSpec {
  id: string;
  name: string;
  description: string;
  files: ToolsetFileEntry[];
}

/** The data side: the knowledge base's file types, and the toolsets written on it. */
export interface ToolsetKnowledgeBase extends Pick<KnowledgeBase, 'getBase' | 'listPreviews' | 'listFileTypes' | 'listFiles'> {
  getToolset(base: string, slug: string): Promise<RemoteToolset | null>;
  putToolset(base: string, spec: ToolsetSpec): Promise<void>;
  /** The data product's promotion of the named toolsets alone: its answer, with nothing applied on a dry run. */
  promoteToolsets(production: string, preview: string, slugs: string[], dryRun: boolean): Promise<unknown>;
  /** A new token bound to one toolset of `base`, as the raw `rec_` secret. */
  mintToolsetToken(base: string, slug: string, name: string): Promise<string>;
}

export interface CutOverOptions {
  orgId: string;
  base: string;
  hubId: string;
  dryRun: boolean;
  yes: boolean;
}

/** What a run wrote, or would have written on a dry run. */
export interface CutOverResult {
  toolsets: Array<{ slug: string; fileTypes: string[]; agents: string[] }>;
  toolsetsWritten: string[];
  toolsetsPromoted: string[];
  connectionsCreated: string[];
  toolsAssigned: string[];
  agentsRewritten: string[];
  automationsRewritten: string[];
  movesRecorded: string[];
  linksDisabled: string[];
  written: boolean;
}

const safe = (text: string) => sanitizeTerminalText(text);

/** A toolset's slug: one per production hub and set of file types, so a re-run finds the same one. */
export function toolsetSlug(productionHubId: string, fileTypes: string[]): string {
  const hash = createHash('sha256').update([productionHubId, ...fileTypes].join('\n')).digest('hex').slice(0, 12);
  return `${CUT_OVER_TOOLSET_PREFIX}${hash}`;
}

function toolsetEntry(fileType: string): ToolsetFileEntry {
  const base = toolBase(fileType);
  return { file_type: fileType, operations: [...FILE_OPERATIONS], ...(base === fileType ? {} : { name: base }) };
}

/** A cut-over toolset's tool for one operation on one file type, named as the data product names it. */
export function toolsetToolName(op: (typeof FILE_OPERATIONS)[number], fileType: string): string {
  return toolsetFileToolName(toolsetEntry(fileType), op);
}

/**
 * `resources/<slug>` at the start of a path: not inside another word or path (an optional leading
 * `/` is kept), and the slug whole (`faq` never matches `faq-2`).
 */
const CITATION_RE = /(?<![\w./-])(\/?)resources\/([a-z0-9]+(?:-[a-z0-9]+)*)(?![a-z0-9_]|-[a-z0-9]|\.\w)/gi;

/** `text` with each `resources/<slug>` of a moved resource read as `data/<file type>` (K13). */
export function rewriteCitations(text: string, fileTypeBySlug: ReadonlyMap<string, string>): string {
  return text.replace(CITATION_RE, (whole, slash: string, slug: string) => {
    const fileType = fileTypeBySlug.get(slug.toLowerCase());
    return fileType ? `${slash}${DATA_NAMESPACE}/${fileType}` : whole;
  });
}

/** The resources an agent is about to lose, as its placeholders name them. */
interface LosingResource {
  slug: string;
  name: string;
}

/**
 * The resource placeholders in `template` that read knowledge in `losing` (K19), as the resolver
 * parses them (`scanTemplateResources`). The scan reports the kinds a catalog call selects only for
 * its deprecated `{{resources()}}` spelling (`legacy_kinds`); `{{files()}}` takes the same
 * arguments, so the template is scanned a second time with each `files(` call spelled
 * `resources(`, and the parser itself says what every catalog call selects.
 */
export function knowledgePlaceholders(template: string, losing: LosingResource[]): string[] {
  if (losing.length === 0 || !template.includes('{{')) return [];
  const scan = scanTemplateResources(template);
  const kinds = scanTemplateResources(template.replace(/\{\{(\s*)files(\s*\()/g, '{{$1resources$2')).legacy_kinds;
  const uses: string[] = [];
  if (kinds === 'all' || kinds === 'kb') uses.push('a {{files()}} catalog that lists knowledge');
  const names = new Set(losing.map((r) => r.name));
  for (const name of scan.resource_content_names) if (names.has(name)) uses.push(`{{resource_content(${name})}}`);
  const slugs = new Set(losing.map((r) => r.slug));
  for (const path of scan.file_content_paths) {
    const [namespace, slug] = cleanRelativePath(path).split('/');
    if (namespace?.toLowerCase() === RESOURCE_NAMESPACE && slug && slugs.has(slug.toLowerCase())) {
      uses.push(`{{file_content(${path})}}`);
    }
  }
  return uses;
}

function sameToolset(remote: RemoteToolset | null, spec: ToolsetSpec): boolean {
  if (!remote) return false;
  const files = (entries: ToolsetFileEntry[] | undefined) =>
    [...(entries ?? [])]
      .map((e) => ({ file_type: e.file_type, operations: [...e.operations].sort(), name: e.name ?? null, path_prefix: e.path_prefix || null }))
      .sort((a, b) => a.file_type.localeCompare(b.file_type));
  return (
    remote.name === spec.name &&
    (remote.description ?? '') === spec.description &&
    (remote.actions ?? []).length === 0 &&
    (remote.relationships ?? []).length === 0 &&
    !remote.batch &&
    !remote.sql_query &&
    isDeepStrictEqual(files(remote.files), files(spec.files))
  );
}

/** One toolset to reach: a set of file types (its spec's) and the agents reading exactly it. */
interface Group {
  slug: string;
  fileTypes: string[];
  agents: CutOverAgent[];
  spec: ToolsetSpec;
  connection: HubConnection | undefined;
  /** Whether this run creates its connection, so its agents' tools are read again once it exists. */
  created: boolean;
}

/** One agent's part: its links to detach, its tools, and its toolset's group, once grouped. */
interface AgentPlan {
  agent: CutOverAgent;
  fileTypes: string[];
  linksToDisable: AgentLink[];
  tools: AgentTools;
  group?: Group;
}

/** The Base Toolset connector's catalog id. */
function toolsetConnectorId(): string {
  const connector = getConnectors().find((c) => isRekorToolsetConnector(c));
  if (!connector) throw new Error('The Base Toolset connector is missing from the catalog');
  return connector.connector_id;
}

/** A cut-over connection's toolset slug, or null for any other connection. */
function cutOverToolsetOf(connection: HubConnection): string | null {
  const toolset = connection.settings?.[TOOLSET_SLUG_SETTING];
  return isRekorToolsetConnectorId(connection.connector_id) && typeof toolset === 'string' && CUT_OVER_TOOLSET_RE.test(toolset)
    ? toolset
    : null;
}

/** The tools `fileTypes`' toolset gives an agent, by name. */
function wantedConnectionTools(fileTypes: string[]): string[] {
  return fileTypes.flatMap((ft) => FILE_OPERATIONS.map((op) => toolsetToolName(op, ft)));
}

/** What an agent lacks of `wanted` on `connectionId`: each tool to add or enable, and each the catalog does not hold yet. */
function toolWork(
  tools: AgentTools,
  wanted: string[],
  connectionId: string | undefined,
): { native: AgentTool[]; connection: AgentTool[]; uncatalogued: string[]; unavailable: string[] } {
  const native: AgentTool[] = [];
  const unavailable: string[] = [];
  for (const name of MOUNT_TOOLS) {
    const row = tools.native.find((t) => t.tool_name === name);
    if (!row) unavailable.push(name);
    else if (row.available_action !== 'Disable') native.push(row);
  }
  const connection: AgentTool[] = [];
  const uncatalogued: string[] = [];
  for (const name of wanted) {
    const row = connectionId ? tools.connection.find((t) => t.connection_id === connectionId && t.tool_name === name) : undefined;
    if (!row) uncatalogued.push(name);
    else if (row.available_action !== 'Disable') connection.push(row);
  }
  return { native, connection, uncatalogued, unavailable };
}

function describeToolWork(work: ReturnType<typeof toolWork>): string[] {
  return [
    ...work.native.map((t) => `${t.available_action === 'Enable' ? 'enable' : 'add'} ${t.tool_name}`),
    ...work.connection.map((t) => `${t.available_action === 'Enable' ? 'enable' : 'add'} ${t.tool_name}`),
    ...work.uncatalogued.map((name) => `add ${name}`),
  ];
}

/** Run one hub's cut-over. */
export async function cutOverHub(hub: HubSetup, kb: ToolsetKnowledgeBase, opts: CutOverOptions, io: MigrateIo): Promise<CutOverResult> {
  const { production, preview } = await resolveBases(kb, opts.base, opts.orgId);
  const stops: string[] = [];

  // ---- The hub --------------------------------------------------------------------------
  const hubs = await hub.listHubs();
  const previewHub = hubs.find((h) => h.hub_id === opts.hubId);
  if (!previewHub) throw expected(`Hub ${safe(opts.hubId)} not found in this organization, or you cannot read it.`);
  if (previewHub.hub_environment !== 'preview') {
    throw expected(`Hub ${safe(opts.hubId)} is a production hub. A hub is cut over on its preview; publishing carries it to production.`);
  }
  const productionHub = previewHub.production_hub_id ? hubs.find((h) => h.hub_id === previewHub.production_hub_id) : undefined;
  if (!productionHub) {
    throw expected(`Hub ${safe(opts.hubId)} has not been published, and its knowledge moved from its production hub. Publish it, then migrate its knowledge first.`);
  }
  const productionHubId = productionHub.hub_id;

  // ---- Which file type each resource moved to ---------------------------------------------
  const fileTypes = await kb.listFileTypes(production);
  const fileTypeBySource = new Map<string, string>();
  for (const ft of fileTypes) {
    const source = provenanceOf(ft.metadata_schema);
    if (source) fileTypeBySource.set(source, ft.id);
  }
  const resources = await hub.listResources(opts.hubId);
  const ownKnowledge = (rows: CutOverResource[]) => rows.filter((r) => r.resource_type === 'knowledge' && !r.org_resource_id);
  const productionOwn = ownKnowledge(await hub.listResources(productionHubId));
  const previewOwn = ownKnowledge(resources);

  // A preview resource is paired with production's by name, exact only while none of them changed.
  // One the preview holds marked moved says its file type, and refuses edits: the diff shows its
  // mark until a publish carries it, which changes no pairing.
  const diff = await hub.hubDiff(opts.hubId);
  const marked = new Set(previewOwn.filter((r) => r.moved_file_type).map((r) => r.resource_name));
  const knowledgeNames = new Set([...previewOwn, ...productionOwn].map((r) => r.resource_name).filter((name) => !marked.has(name)));
  const changed = [
    ...diff.resources.filter((e) => knowledgeNames.has(e.name)).map((e) => `resource "${e.name}" (${e.status})`),
    ...(diff.resource_folders ?? []).filter((e) => e.type && knowledgeNames.has(e.type)).map((e) => `a folder of "${e.type}"`),
    ...(diff.resource_files ?? []).filter((e) => e.type && knowledgeNames.has(e.type)).map((e) => `a file of "${e.type}"`),
  ];
  if (changed.length > 0) {
    stops.push(
      `The preview's knowledge resources differ from production's, which moved (${[...new Set(changed)].join(', ')}): ` +
        'sync or discard those changes first, so its agents are cut over to what they read.',
    );
  }

  const fileTypeOf = new Map<string, string>();
  for (const resource of resources) {
    if (resource.resource_type !== 'knowledge') continue;
    if (resource.org_resource_id) {
      const ft = fileTypeBySource.get(orgSourceKey(resource.org_resource_id));
      if (ft) fileTypeOf.set(resource.resource_id, ft);
      continue;
    }
    if (resource.moved_file_type) {
      fileTypeOf.set(resource.resource_id, resource.moved_file_type);
      continue;
    }
    const twins = productionOwn.filter((r) => r.resource_name === resource.resource_name);
    const previewTwins = previewOwn.filter((r) => r.resource_name === resource.resource_name);
    if (twins.length > 1 || previewTwins.length > 1) {
      stops.push(`Two knowledge resources are named "${resource.resource_name}": rename one (in the preview, then sync), so each pairs with its production copy.`);
      continue;
    }
    const ft = twins[0] ? fileTypeBySource.get(hubSourceKey(productionHubId, twins[0].resource_id)) : undefined;
    if (ft) fileTypeOf.set(resource.resource_id, ft);
  }

  // A hub part two production hubs share names neither one; the move route refuses it (#5083 decision 149).
  const productionHubs = hubs.filter((h) => h.hub_environment === 'production');
  for (const resource of previewOwn) {
    const ft = fileTypeOf.get(resource.resource_id);
    if (!ft || resource.moved_file_type) continue;
    const hubPart = hubPartOf(ft);
    const holders = productionHubs.filter((h) => slugifySkillName(h.hub_name) === hubPart);
    if (holders.length > 1) {
      stops.push(
        `File type "${ft}" names the hub slug "${hubPart}", which ${holders.length} production hubs share ` +
          `(${holders.map((h) => `"${h.hub_name}"`).join(', ')}): its move can't be recorded until all but one are renamed.`,
      );
    }
  }

  // `resources/<slug>` → `data/<file type>`, by the slugs the preview's file tools derive today.
  const slugById = buildResourceSlugMap(resources);
  const fileTypeBySlug = new Map<string, string>();
  for (const [resourceId, ft] of fileTypeOf) {
    const slug = slugById.get(resourceId);
    if (slug) fileTypeBySlug.set(slug, ft);
  }
  const byId = new Map(resources.map((r) => [r.resource_id, r]));

  // ---- The cut-over toolsets the hub already reaches ---------------------------------------
  const connections = await hub.listConnections(opts.hubId);
  const previewToolsets = new Map<string, RemoteToolset | null>();
  const connectionBySlug = new Map<string, HubConnection>();
  for (const connection of connections) {
    const slug = cutOverToolsetOf(connection);
    if (!slug) continue;
    connectionBySlug.set(slug, connection);
    if (!previewToolsets.has(slug)) previewToolsets.set(slug, await kb.getToolset(preview, slug));
  }
  const toolsetTypes = (slug: string) => (previewToolsets.get(slug)?.files ?? []).map((e) => e.file_type);
  const slugByConnection = new Map([...connectionBySlug].map(([slug, c]) => [c.connection_id, slug]));

  // ---- Each agent's set (K9) and placeholders (K19) ----------------------------------------
  const agents = await hub.listAgents(opts.hubId);
  const plans: AgentPlan[] = [];
  for (const agent of agents) {
    const links = (await hub.listAgentLinks(opts.hubId, agent.agent_id)).filter((l) => l.resource_type === 'knowledge');
    const enabled = links.filter((l) => l.enabled);
    const tools = await hub.listAgentTools(opts.hubId, agent.agent_id);
    const set = new Set<string>();
    for (const link of enabled) {
      const ft = fileTypeOf.get(link.resource_id);
      const resource = byId.get(link.resource_id);
      if (!ft) {
        stops.push(
          `Agent "${agent.agent_name}" reads knowledge resource "${resource?.resource_name ?? link.resource_id}", which has not moved: ` +
            `move it first (\`wayai knowledge migrate --org ${opts.orgId} --base ${opts.base}${resource?.org_resource_id ? '' : ` --hub ${productionHubId}`}\`).`,
        );
        continue;
      }
      set.add(ft);
    }
    // The cut-over toolsets it holds a tool on already: a partial run's.
    for (const tool of tools.connection) {
      const slug = tool.connection_id ? slugByConnection.get(tool.connection_id) : undefined;
      if (slug && tool.available_action === 'Disable') for (const ft of toolsetTypes(slug)) set.add(ft);
    }
    const runner = connections.find((c) => c.connection_id === agent.connection_id);
    if (enabled.length > 0 && runner && getConnectorById(runner.connector_id)?.is_harness === true) {
      stops.push(
        `Agent "${agent.agent_name}" runs on a hosted harness, which takes no Base Toolset's tools yet, so it would lose its knowledge: ` +
          'connect it to a toolset through an MCP Server connection, or detach its knowledge links, then run again.',
      );
    }
    const losing = enabled.flatMap((link) => {
      const resource = byId.get(link.resource_id);
      const slug = slugById.get(link.resource_id);
      return resource && slug ? [{ slug, name: resourceDisplayName(resource) }] : [];
    });
    for (const [field, template] of [
      ['instructions', agent.instructions],
      ['additional context', agent.additional_context_template],
    ] as const) {
      for (const use of knowledgePlaceholders(template ?? '', losing)) {
        stops.push(
          `Agent "${agent.agent_name}" uses ${use} in its ${field}, which reads nothing once its knowledge moves (K19): ` +
            'move that text into its instructions or a skill, then run again.',
        );
      }
    }
    if (set.size > 0 || enabled.length > 0) plans.push({ agent, fileTypes: [...set].sort(), linksToDisable: enabled, tools });
  }

  // ---- The toolsets, one per distinct set ------------------------------------------------
  const groups = new Map<string, Group>();
  for (const plan of plans) {
    if (plan.fileTypes.length === 0) continue;
    const slug = toolsetSlug(productionHubId, plan.fileTypes);
    const group = groups.get(slug);
    if (group) {
      group.agents.push(plan.agent);
      plan.group = group;
      continue;
    }
    plan.group = {
      slug,
      fileTypes: plan.fileTypes,
      agents: [plan.agent],
      connection: connectionBySlug.get(slug),
      created: !connectionBySlug.has(slug),
      spec: {
        id: slug,
        name: `Knowledge of ${previewHub.hub_name}`.slice(0, 200),
        description:
          `Read-only knowledge for agents of hub ${productionHubId}, written by wayai knowledge cut-over: ` +
          `list and get on ${plan.fileTypes.join(', ')}.`,
        files: plan.fileTypes.map(toolsetEntry),
      },
    };
    groups.set(slug, plan.group);
  }

  // The preview hub's toolsets read the preview base, which a migration never writes (O3): each
  // file production holds for a planned file type must be there, copied by a refresh (K10, K14).
  const plannedTypes = [...new Set([...groups.values()].flatMap((g) => g.fileTypes))].sort();
  const missing: string[] = [];
  for (const ft of plannedTypes) {
    const [inProduction, inPreview] = await Promise.all([kb.listFiles(production, ft), filesOrNone(kb, preview, ft)]);
    const there = new Set(inPreview.map((f) => f.path));
    for (const file of inProduction) if (!there.has(file.path)) missing.push(`${ft}/${file.path}`);
  }
  if (missing.length > 0) {
    stops.push(
      `The preview base "${preview}" lacks ${missing.length} file(s) production holds (${missing.slice(0, 5).join(', ')}${missing.length > 5 ? ', …' : ''}), ` +
        `so the preview's agents would read none of them: copy them in with \`${refreshPreviewCommand(opts.orgId, opts.base)}\`, then run again.`,
    );
  }

  // Each toolset is written in the preview unless it is there as planned, and promoted unless
  // production holds it as planned.
  const toolsetsToWrite: ToolsetSpec[] = [];
  const toolsetsToPromote: ToolsetSpec[] = [];
  for (const group of groups.values()) {
    const [inPreview, inProduction] = await Promise.all([
      previewToolsets.has(group.slug) ? previewToolsets.get(group.slug)! : kb.getToolset(preview, group.slug),
      kb.getToolset(production, group.slug),
    ]);
    if (!sameToolset(inPreview, group.spec)) toolsetsToWrite.push(group.spec);
    if (!sameToolset(inProduction, group.spec)) toolsetsToPromote.push(group.spec);
  }
  const connectionsToCreate = [...groups.values()].filter((g) => g.created);

  // ---- Each agent's tools, text and links ------------------------------------------------
  const toolLines: string[] = [];
  let toolWrites = 0;
  for (const plan of plans) {
    const group = plan.group;
    if (!group) continue;
    const work = toolWork(plan.tools, wantedConnectionTools(group.fileTypes), group.connection?.connection_id);
    for (const name of work.unavailable) stops.push(`Agent "${plan.agent.agent_name}" can't be given ${name}: the hub offers it no such tool.`);
    const lines = describeToolWork(work);
    toolWrites += lines.length;
    if (lines.length > 0) toolLines.push(`  ${safe(plan.agent.agent_name)}: ${lines.map(safe).join(', ')} (${group.slug})`);
  }

  const agentRewrites: Array<{ agent: CutOverAgent; patch: { instructions?: string; additional_context_template?: string } }> = [];
  for (const agent of agents) {
    const patch: { instructions?: string; additional_context_template?: string } = {};
    const instructions = rewriteCitations(agent.instructions ?? '', fileTypeBySlug);
    if (instructions !== (agent.instructions ?? '')) patch.instructions = instructions;
    const context = rewriteCitations(agent.additional_context_template ?? '', fileTypeBySlug);
    if (context !== (agent.additional_context_template ?? '')) patch.additional_context_template = context;
    if (Object.keys(patch).length > 0) agentRewrites.push({ agent, patch });
  }
  const automationRewrites: Array<{ automation: HubAutomation; action: AutomationAction }> = [];
  for (const automation of await hub.listAutomations(opts.hubId)) {
    const text = automation.action.type === 'run_agent' ? automation.action.instructions : null;
    if (!text) continue;
    const rewritten = rewriteCitations(text, fileTypeBySlug);
    if (rewritten !== text) automationRewrites.push({ automation, action: { ...automation.action, instructions: rewritten } });
  }

  // The hub's own moved resources, and each org resource whose link this run detaches.
  const moves: Array<{ body: RecordKnowledgeMoveInput; label: string }> = [];
  for (const resource of previewOwn) {
    const ft = fileTypeOf.get(resource.resource_id);
    if (!ft || resource.moved_file_type) continue;
    moves.push({
      body: { scope: 'hub', hub_id: opts.hubId, resource_id: resource.resource_id, file_type: ft, base_id: production },
      label: `hub resource "${resource.resource_name}" → ${ft}`,
    });
  }
  const orgRecorded = new Set<string>();
  for (const plan of plans) {
    for (const link of plan.linksToDisable) {
      const resource = byId.get(link.resource_id);
      const ft = fileTypeOf.get(link.resource_id);
      if (!resource?.org_resource_id || !ft || orgRecorded.has(resource.org_resource_id)) continue;
      orgRecorded.add(resource.org_resource_id);
      moves.push({
        body: { scope: 'organization', organization_id: opts.orgId, org_resource_id: resource.org_resource_id, file_type: ft, base_id: production },
        label: `organization resource "${resource.resource_name}" → ${ft}`,
      });
    }
  }
  const links = plans.flatMap((plan) => plan.linksToDisable.map((link) => ({ plan, link })));

  // ---- Report -----------------------------------------------------------------------------
  const result: CutOverResult = {
    toolsets: [...groups.values()].map((g) => ({ slug: g.slug, fileTypes: g.fileTypes, agents: g.agents.map((a) => a.agent_name) })),
    toolsetsWritten: toolsetsToWrite.map((s) => s.id),
    toolsetsPromoted: toolsetsToPromote.map((s) => s.id),
    connectionsCreated: connectionsToCreate.map((g) => g.slug),
    toolsAssigned: toolLines,
    agentsRewritten: agentRewrites.map((r) => r.agent.agent_name),
    automationsRewritten: automationRewrites.map((r) => r.automation.name),
    movesRecorded: moves.map((m) => m.label),
    linksDisabled: links.map(({ plan, link }) => `${plan.agent.agent_name}: ${byId.get(link.resource_id)?.resource_name ?? link.resource_id}`),
    written: false,
  };

  io.log(`Cut-over of hub "${safe(previewHub.hub_name)}" (${opts.hubId}) to base "${safe(production)}" (toolsets written in "${safe(preview)}")`);
  io.log('\nToolsets (read-only: list and get):');
  if (groups.size === 0) io.log('  (none: no agent reads moved knowledge)');
  for (const group of groups.values()) {
    const mark = toolsetsToPromote.includes(group.spec) || toolsetsToWrite.includes(group.spec) ? '+' : '=';
    io.log(`  ${mark} ${group.slug}: ${group.fileTypes.map(safe).join(', ')} — ${group.agents.map((a) => safe(a.agent_name)).join(', ')}`);
  }
  io.log('\nConnections:');
  for (const group of groups.values()) {
    io.log(`  ${group.connection ? '=' : '+'} Base Toolset "${group.slug}"${group.connection ? '' : ', with a new token bound to it on the preview base'}`);
  }
  io.log('\nAgent tools:');
  if (toolLines.length === 0) io.log('  (nothing to assign)');
  for (const line of toolLines) io.log(line);
  io.log('\nPaths rewritten to data/ (K13):');
  if (agentRewrites.length + automationRewrites.length === 0) io.log('  (none)');
  for (const { agent, patch } of agentRewrites) io.log(`  agent "${safe(agent.agent_name)}": ${Object.keys(patch).join(', ')}`);
  for (const { automation } of automationRewrites) io.log(`  automation "${safe(automation.name)}": instructions`);
  io.log('\nMoves recorded:');
  if (moves.length === 0) io.log('  (none)');
  for (const move of moves) io.log(`  + ${safe(move.label)}`);
  io.log('\nKnowledge links disabled:');
  if (links.length === 0) io.log('  (none)');
  for (const line of result.linksDisabled) io.log(`  - ${safe(line)}`);

  if (stops.length > 0) {
    io.log('\nStopped:');
    for (const stop of stops) io.log(`  ${safe(stop)}`);
    throw expected(`The cut-over stops on ${stops.length} reason(s) above; nothing was written.`);
  }

  const writes =
    toolsetsToWrite.length + toolsetsToPromote.length + connectionsToCreate.length + toolWrites +
    agentRewrites.length + automationRewrites.length + moves.length + links.length;
  const printProduction = () => printProductionSteps(io, opts, production, productionHubId, [...groups.values()]);
  if (opts.dryRun) {
    io.log(`\nDry run: nothing written (${writes === 0 ? 'nothing to write' : `${writes} write(s) planned`}).`);
    printProduction();
    return result;
  }
  if (writes === 0) {
    io.log('\nNothing to write: this hub is cut over.');
    printProduction();
    return result;
  }
  if (!opts.yes && !(await io.confirm(`Make these ${writes} change(s) to the preview hub and its knowledge base?`))) {
    io.log('Cancelled.');
    return result;
  }

  // ---- Write: the moves first ------------------------------------------------------------------
  // The organization's registry may refuse one (K8: a file type id another resource or hub
  // holds), which only it can judge: a refusal then stops the run before any toolset, connection,
  // tool, text or link is written. The moves recorded ahead of it stay: a re-run skips the hub's own
  // (marked moved). The links are disabled last, so any re-run before them finds them still
  // enabled and sends the org resources' moves again (a held move writes nothing).
  for (const move of moves) await hub.recordMove(move.body);

  // ---- The toolsets -----------------------------------------------------------------------
  for (const spec of toolsetsToWrite) await kb.putToolset(preview, spec);
  if (toolsetsToPromote.length > 0) {
    const slugs = toolsetsToPromote.map((s) => s.id);
    const problems = promotionProblems(await kb.promoteToolsets(production, preview, slugs, true), new Set(slugs), { section: 'toolsets' });
    if (problems.length > 0) {
      throw expected(
        `The promotion into "${safe(production)}" would not apply as planned (${problems.map(safe).join('; ')}). ` +
          `The toolsets are written in "${safe(preview)}"; nothing reached production, and the hub's agents are unchanged.`,
      );
    }
    await kb.promoteToolsets(production, preview, slugs, false);
  }

  // ---- The connections --------------------------------------------------------------------
  if (connectionsToCreate.length > 0) {
    const connectorId = toolsetConnectorId();
    for (const group of connectionsToCreate) {
      const token = await kb.mintToolsetToken(preview, group.slug, `${group.slug} (hub ${opts.hubId})`);
      const after = await hub.addToolsetConnection(opts.hubId, { connectorId, name: group.slug, toolset: group.slug, token });
      group.connection = after.find((c) => cutOverToolsetOf(c) === group.slug);
      if (!group.connection) throw new Error(`The Base Toolset connection "${group.slug}" was not found after it was created.`);
    }
  }

  // ---- Each agent's tools: the mount's two file tools, and its toolset's list_ and get_ ----
  const synced = new Set<string>();
  for (const plan of plans) {
    const group = plan.group;
    if (!group?.connection) continue;
    const connectionId = group.connection.connection_id;
    const wanted = wantedConnectionTools(group.fileTypes);
    // The tools read while planning are current, unless this run created or synced the connection since.
    const stale = group.created || synced.has(connectionId);
    let tools = stale ? await hub.listAgentTools(opts.hubId, plan.agent.agent_id) : plan.tools;
    let work = toolWork(tools, wanted, connectionId);
    if (work.uncatalogued.length > 0 && !synced.has(connectionId)) {
      await hub.syncConnection(opts.hubId, connectionId);
      synced.add(connectionId);
      tools = await hub.listAgentTools(opts.hubId, plan.agent.agent_id);
      work = toolWork(tools, wanted, connectionId);
    }
    if (work.uncatalogued.length > 0) {
      throw new Error(
        `Connection "${group.slug}" catalogs no ${safe(work.uncatalogued.join(', '))} after a sync, so agent "${safe(plan.agent.agent_name)}" can't be given them.`,
      );
    }
    for (const tool of [...work.native, ...work.connection]) {
      if (tool.available_action === 'Enable' && tool.tool_id) await hub.enableTool(opts.hubId, plan.agent.agent_id, tool.tool_id);
      else if (tool.tool_native_id) await hub.addNativeTool(opts.hubId, plan.agent.agent_id, tool.tool_native_id);
      else if (tool.mcp_tool_id) await hub.addConnectionTool(opts.hubId, plan.agent.agent_id, tool.mcp_tool_id);
    }
  }

  // ---- The paths, then the links ---------------------------------------------------------------
  for (const { agent, patch } of agentRewrites) await hub.updateAgentText(opts.hubId, agent.agent_id, patch);
  for (const { automation, action } of automationRewrites) await hub.updateAutomationAction(opts.hubId, automation.automation_id, action);
  for (const { plan, link } of links) await hub.disableLink(opts.hubId, plan.agent.agent_id, link.resource_id);

  io.log(`\nDone: the preview hub's agents read their knowledge from "${safe(preview)}" through ${groups.size} toolset(s).`);
  printProduction();
  return { ...result, written: true };
}

/** A base's files of one file type, or none when the base does not hold the file type. */
async function filesOrNone(kb: Pick<ToolsetKnowledgeBase, 'listFiles'>, base: string, fileType: string) {
  try {
    return await kb.listFiles(base, fileType);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return [];
    throw err;
  }
}

/** What production needs, printed for the operator: this command never writes production. */
function printProductionSteps(io: MigrateIo, opts: CutOverOptions, production: string, productionHubId: string, groups: Group[]): void {
  if (groups.length === 0) return;
  io.log('\nProduction (not run by this command), once the preview passes its evals:');
  io.log(`  1. Publish the preview: wayai publish --hub ${opts.hubId}`);
  io.log('  2. For each toolset, mint a token bound to it on the production base, and save it on production\'s connection:');
  for (const group of groups) {
    io.log(
      `     wayai bases tokens create-for-toolset ${group.slug} --base ${safe(production)} --token-only | ` +
        `wayai set-connection-credential --hub ${productionHubId} --connection "${group.slug}" --field access_token --stdin`,
    );
  }
}
